import { useState } from "react";
import { api } from "../lib/api";
import { useFetch } from "../lib/useFetch";

interface Member { id: number; name: string; email: string; role: string }

const field = "input";
const roleStyle: Record<string, string> = {
  owner: "bg-slate-800 text-white",
  coordinator: "bg-teal-50 text-teal-700",
  worker: "bg-sky-50 text-sky-700",
  finance: "bg-amber-50 text-amber-700",
};

export default function Team() {
  const { data, error, loading, reload } = useFetch<Member[]>("/v1/users");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "worker" });
  const [formError, setFormError] = useState<string | null>(null);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (form.password.length < 10) { setFormError("Password must be at least 10 characters"); return; }
    try {
      await api("/v1/users", { method: "POST", body: JSON.stringify(form) });
      setForm({ name: "", email: "", password: "", role: "worker" });
      setShowForm(false); setFormError(null); reload();
    } catch (err) { setFormError((err as Error).message); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink">Team</h1>
        <button onClick={() => setShowForm((v) => !v)}
          className="btn-primary">
          {showForm ? "Cancel" : "Add team member"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="card p-5 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
          <label className="block"><span className="label">Name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} /></label>
          <label className="block"><span className="label">Email</span>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} /></label>
          <label className="block"><span className="label">Temporary password</span>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={field} /></label>
          <label className="block"><span className="label">Role</span>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={field}>
              <option value="worker">Support worker</option>
              <option value="coordinator">Coordinator</option>
              <option value="finance">Finance / HR</option>
            </select></label>
          <button className="btn-dark">Save</button>
          {formError && <p className="text-sm text-red-600 sm:col-span-5">{formError}</p>}
        </form>
      )}

      {loading && <div className="space-y-3"><div className="skeleton h-24" /><div className="skeleton h-24" /></div>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {data && (
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-edge-soft">
                <th className="th">Name</th>
                <th className="th">Email</th>
                <th className="th">Role</th>
              </tr>
            </thead>
            <tbody>
              {data.map((m) => (
                <tr key={m.id} className="border-b border-edge-soft last:border-0">
                  <td className="px-5 py-3 font-medium text-ink">{m.name}</td>
                  <td className="px-5 py-3 text-mut">{m.email}</td>
                  <td className="px-5 py-3">
                    <span className={`chip ${roleStyle[m.role] ?? "bg-slate-100 text-body"}`}>{m.role}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-sm text-faint">Production replaces temporary passwords with invite emails (FR-IAM-05).</p>
    </div>
  );
}
